import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// project imports
import { useAppSelector, useAppDispatch } from 'hooks/useAppStore';
import useConfig from 'hooks/useConfig';
import {
  selectAccountInfo,
  selectRealm,
  selectSelectedEmsMemberAccount
} from 'features/account/accountSelectors';
import { sendAuditLog } from 'features/app/appThunks';
import {
  accountLogout,
  setSelectedEmsMemberAccount
} from 'features/account/accountSlice';
import { revokeAccessToken } from 'features/account/accountThunks';
import { setEmsMemberAccountList } from 'features/eai/eaiSlice';
import { selectEmsMemberAccountList } from 'features/eai/selectors/emsMemberAccountSelectors';

// assets
import { IconLogout, IconSettings, IconUsers } from '@tabler/icons';
import MainCard from 'components/cards/MainCard';
import Transitions from 'components/assets/Transitions';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  ClickAwayListener,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { Config } from 'utils/envConfig';
import useTenant from 'hooks/useTenant';
import { useKeycloak } from 'contexts/KeycloakContext';
import { fetchAndSetEmsMemberAccountList } from 'features/eai/thunks/emsMemberAccountThunks';
import useUserGroupNames from 'hooks/useUserGroupNames';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
  const dispatch = useAppDispatch();
  const { borderRadius, navType, onChangeMenuType } = useConfig();
  const { keycloak } = useKeycloak();
  const theme = useTheme();
  const { t } = useTranslation();

  // const [selectedIndex, setSelectedIndex] = useState(-1);
  const accountInfo = useAppSelector(selectAccountInfo);
  const selectedEmsMemberAccount = useAppSelector(
    selectSelectedEmsMemberAccount
  );
  const emsMemberAccountList = useAppSelector(selectEmsMemberAccountList);
  const existingRealm = useAppSelector(selectRealm);
  const userGroupNames = useUserGroupNames(accountInfo?.userGroups);

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<any>(null);

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (existingRealm && !!accountInfo) {
      dispatch(fetchAndSetEmsMemberAccountList(true, undefined, false));
    }
  }, [existingRealm, accountInfo, dispatch]);

  const { tenant } = useTenant();

  const handleLogout = async () => {
    dispatch(
      sendAuditLog({ message: 'User Logout onAuthLogout' }, () =>
        revokeAccessToken(keycloak?.refreshToken, () => {
          dispatch(setEmsMemberAccountList([]));
          dispatch(accountLogout);
          keycloak?.logout({
            redirectUri: Config.clientUrl + '/' + tenant
          });
        })
      )
    );
  };

  const handleChangeSubject = () => {
    dispatch(setSelectedEmsMemberAccount(null));
  };

  const handleToggle = () => {
    setOpen((previousValue) => !previousValue);
  };

  const handleClose = (
    event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent
  ) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const handleSwitch = ({
    target: { checked, name }
  }: React.ChangeEvent<HTMLInputElement>) => {
    switch (name) {
      case 'sdm':
        onChangeMenuType(checked ? 'dark' : 'light');
        break;
    }
  };

  return (
    <>
      <Chip
        sx={{
          height: '48px',
          alignItems: 'center',
          borderRadius: '27px',
          transition: 'all .2s ease-in-out',
          borderColor:
            theme.palette.mode === 'dark'
              ? theme.palette.dark.main
              : theme.palette.primary.light,
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.dark.main
              : theme.palette.primary.light,
          '&[aria-controls="menu-list-grow"], &:hover': {
            borderColor: theme.palette.primary.main,
            background: `${theme.palette.primary.main}!important`,
            color: theme.palette.primary.light,
            '& svg': {
              stroke: theme.palette.primary.light
            }
          },
          '& .MuiChip-label': {
            lineHeight: 0
          }
        }}
        icon={
          <Avatar
            sx={{
              ...theme.typography.mediumAvatar,
              margin: '8px 0 8px 8px !important',
              cursor: 'pointer'
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup='true'
            //color='inherit'
          >
            <Person />
          </Avatar>
        }
        label={
          <IconSettings
            stroke={1.5}
            size='24px'
            color={theme.palette.primary.main}
          />
        }
        variant='outlined'
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup='true'
        onClick={handleToggle}
        color='primary'
      />

      <Popper
        placement='bottom'
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 14]
            }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper>
                {open && (
                  <MainCard
                    border={false}
                    elevation={16}
                    content={false}
                    boxShadow
                    shadow={theme.shadows[16]}
                  >
                    <Box sx={{ p: 2, pb: 1.4 }}>
                      <Stack>
                        <Stack
                          direction='row'
                          spacing={3}
                          alignItems='flex-end'
                          justifyContent={'space-between'}
                        >
                          <Typography
                            component='span'
                            variant='h4'
                            sx={{ fontWeight: 400 }}
                          >
                            {`${accountInfo?.given_name} ${accountInfo?.family_name}`}
                          </Typography>
                          <Typography
                            component='span'
                            variant='h4'
                            sx={{ fontWeight: 400 }}
                          >
                            {selectedEmsMemberAccount?.emsPartyName}
                          </Typography>
                        </Stack>
                        <Stack
                          direction='row'
                          spacing={0.5}
                          alignItems='flex-end'
                          justifyContent={'space-between'}
                        >
                          <Typography variant='subtitle2'>
                            {accountInfo?.email}
                          </Typography>
                          <Typography variant='subtitle2'>
                            {selectedEmsMemberAccount?.accountType
                              ? t(
                                  `module.eai.codebooks.${selectedEmsMemberAccount?.accountType}.title`
                                )
                              : ''}
                          </Typography>
                        </Stack>
                        <Typography variant='subtitle2'>
                          {userGroupNames}
                        </Typography>
                      </Stack>
                    </Box>

                    <Box sx={{ p: 2, pt: 0 }}>
                      <Divider />
                      <Card
                        sx={{
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.dark[800]
                              : theme.palette.primary.light,
                          my: 2
                        }}
                      >
                        <CardContent>
                          <Grid container spacing={3} direction='column'>
                            <Grid item>
                              <Grid
                                item
                                container
                                alignItems='center'
                                justifyContent='space-between'
                              >
                                <Grid item>
                                  <Typography variant='subtitle1'>
                                    {t('header.profile.darkMode')}
                                  </Typography>
                                </Grid>
                                <Grid item>
                                  <Switch
                                    color='primary'
                                    checked={navType === 'dark'}
                                    onChange={(e) => handleSwitch(e)}
                                    name='sdm'
                                    size='small'
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>

                      <Divider />

                      <List
                        component='nav'
                        sx={{
                          width: '100%',
                          maxWidth: 350,
                          minWidth: 300,
                          backgroundColor: theme.palette.background.paper,
                          borderRadius: '10px',
                          [theme.breakpoints.down('md')]: {
                            minWidth: '100%'
                          },
                          '& .MuiListItemButton-root': {
                            mt: 0.5
                          }
                        }}
                      >
                        {emsMemberAccountList.length > 1 && (
                          <ListItemButton
                            sx={{ borderRadius: `${borderRadius}px` }}
                            selected={/*selectedIndex === 4*/ false}
                            onClick={handleChangeSubject}
                          >
                            <ListItemIcon>
                              <IconUsers stroke={1.5} size='20px' />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant='body2'>
                                  {t('header.profile.changeSubject')}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        )}

                        <ListItemButton
                          sx={{ borderRadius: `${borderRadius}px` }}
                          selected={/*selectedIndex === 4*/ false}
                          onClick={handleLogout}
                        >
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size='20px' />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant='body2'>
                                {t('header.profile.logout')}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </List>

                      <Divider />
                    </Box>

                    <Box sx={{ p: 2, pt: 0 }}>
                      <Typography variant='subtitle2' textAlign='center'>
                        {t('header.profile.version')}: {Config.releaseVersion}
                      </Typography>
                    </Box>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default ProfileSection;
